import React from 'react';

// 운영 DB에 직접 저장됐을 수 있는 placeholder/dummy 값.
// 이런 값이 들어오면 빈값과 동일하게 취급해 토큰 자동 숨김.
const PLACEHOLDER_VALUES = new Set([
  '준비중', '미입력', '없음', '-', '0', '000-0000-0000', '000-00-00000',
]);

function isValidValue(v) {
  if (!v) return false;
  const trimmed = String(v).trim();
  if (trimmed === '') return false;
  if (PLACEHOLDER_VALUES.has(trimmed)) return false;
  return true;
}

/**
 * 셀러(판매자) 사업자 정보 표시 컴포넌트.
 *
 * 사업자가 없는 개인 판매자도 가입할 수 있도록 값이 비어 있는 항목은
 * 라벨·구분자(`|`)까지 통째로 숨김 처리한다.
 *
 * 필드가 모두 비어 있으면 컴포넌트 자체를 렌더링하지 않는다.
 *
 * props:
 *  - seller:        Seller 객체 (name, businessNumber, contactPhone, contactEmail, email, address, shopName)
 *  - displayName:   타이틀 prefix (예: 가게명). 기본값 seller.shopName || "판매자 정보"
 *  - align:         'center' | 'left' (기본 'left')
 *  - className:     외부 컨테이너에 추가할 className
 */
function SellerBusinessInfo({ seller, displayName, align = 'left', className = '' }) {
  if (!seller) return null;

  const title = displayName || seller.shopName || '판매자 정보';
  const contactEmail = isValidValue(seller.contactEmail) ? seller.contactEmail : null;
  const email = contactEmail || seller.email;

  // 각 항목을 (라벨, 값) 토큰으로 모은 다음 값이 falsy(빈문자열/null/undefined)이거나
  // placeholder 값인 항목은 제외.
  const tokens = [
    { label: '대표', value: seller.name },
    { label: '사업자등록번호', value: seller.businessNumber },
    { label: '연락처', value: seller.contactPhone },
    { label: '이메일', value: email },
  ].filter(t => isValidValue(t.value));

  const addressValid = isValidValue(seller.address);

  // 모든 항목이 비어 있고 주소도 없으면 사업자 정보 블록 자체를 숨긴다.
  if (tokens.length === 0 && !addressValid) return null;

  const justify = align === 'center' ? 'justify-center md:justify-center' : 'justify-center md:justify-start';
  const textAlign = align === 'center' ? 'text-center' : 'text-center md:text-left';

  return (
    <div className={`${textAlign} ${className}`}>
      <p className="font-bold mb-2 text-gray-700 dark:text-gray-300">{title} (판매자)</p>

      {tokens.length > 0 && (
        <div className={`flex flex-wrap gap-x-3 gap-y-1 ${justify} leading-relaxed`}>
          {tokens.map((t, idx) => (
            <React.Fragment key={t.label}>
              {idx > 0 && <span className="text-gray-300">|</span>}
              <span>{t.label}: {t.value}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {addressValid && (
        <p className="mt-1">주소: {seller.address}</p>
      )}
    </div>
  );
}

export default SellerBusinessInfo;
